import React from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import { useTranslation } from 'react-i18next';

import { GatsbySanityImageFluid } from '../types';
import WEATHER_CONFIG from '../constants/weatherConfig';
import GenericLink from './GenericLink';
import WeatherWidget from './WeatherWidget/WeatherWidgetContainer';
import ReadMoreLink from './ReadMoreLink';
import nerthusLogoGreen from '../assets/images/nerthusLogoGreen.svg';
import { NERTHUS_LINK, NERTHUS_LINKS_ENABLED } from '../constants';

import * as styles from './WidgetSection.module.scss';

interface QueryData {
  sanityHomepage: {
    widgetsSection: {
      antiPoisonName: string;
      antiPoisonNumber: string;
      bottomWidgetUrl?: string;
      bottomWidgetImage?: {
        asset: {
          fluid: GatsbySanityImageFluid;
        };
      };
    };
  };
}

const WidgetSection = (): React.ReactElement => {
  const data: QueryData = useStaticQuery(
    graphql`
      {
        sanityHomepage {
          widgetsSection {
            antiPoisonName
            antiPoisonNumber
            bottomWidgetUrl
            bottomWidgetImage {
              asset {
                fluid(maxWidth: 420) {
                  ...GatsbySanityImageFluid
                }
              }
            }
          }
        }
      }
    `,
  );
  const { widgetsSection } = data.sanityHomepage || {};

  const { t } = useTranslation();

  function renderBottomWidget(className: string): React.ReactElement | null {
    return widgetsSection.bottomWidgetUrl &&
      widgetsSection.bottomWidgetImage &&
      widgetsSection.bottomWidgetImage.asset ? (
      <div className={styles.bottomWidgetWrapper + ' ' + className}>
        <a href={widgetsSection.bottomWidgetUrl}>
          <Img
            className={styles.bottomWidgetImage}
            fluid={widgetsSection.bottomWidgetImage.asset.fluid}
            alt="Widget"
          />
        </a>
      </div>
    ) : null;
  }

  return (
    <section className={styles.section}>
      {widgetsSection && (
        <div className={styles.antiPoisonCenter}>
          <span className={styles.antiPoisonName}>{widgetsSection.antiPoisonName}</span>
          <div className={styles.antiPoisonNumberWrapper}>
            <GenericLink
              noStyle
              className={styles.antiPoisonNumber}
              to={'tel:' + widgetsSection.antiPoisonNumber}
            >
              {widgetsSection.antiPoisonNumber}
            </GenericLink>
          </div>
          {renderBottomWidget(
            styles.belowAntiPoisonNumber + (!WEATHER_CONFIG ? ' ' + styles.noWeatherWidget : ''),
          )}
        </div>
      )}
      <div className={styles.widgets}>
        {WEATHER_CONFIG && (
          <div className={styles.weatherWidgetWrapper}>
            <WeatherWidget
              cities={WEATHER_CONFIG.cities}
              units={WEATHER_CONFIG.units}
              lang={WEATHER_CONFIG.lang}
            ></WeatherWidget>
          </div>
        )}
        {widgetsSection &&
          renderBottomWidget(
            styles.belowWeatherWidget + (!WEATHER_CONFIG ? ' ' + styles.noWeatherWidget : ''),
          )}
        {NERTHUS_LINKS_ENABLED && (
          <div className={styles.nerthusContainer}>
            <span className={styles.title}>
              {t('nerthus.project_name', 'Nerthus Sustainability')}
            </span>
            <p className={styles.subtitle}>
              {t('nerthus.info', 'Sustainability and Social Responsibility')}
            </p>
            <ReadMoreLink to={NERTHUS_LINK} className={styles.button}>
              {t('nerthus.button_label', 'Know More')}
            </ReadMoreLink>
          </div>
        )}
      </div>
    </section>
  );
};

export default WidgetSection;
